import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import BackgroundShapes from '../common/BackgroundShapes';
import { getPageUrl } from '../../utils/urlHelpers';
import { condoService } from '../../services/condoService';

interface QuickSignupPageProps {
  onNavigate: (page: string) => void;
}

// Signup page URL configuration
export const signupUrl = getPageUrl('/signup');

const QuickSignupPage: React.FC<QuickSignupPageProps> = ({ onNavigate }) => {
  const { quickSignup, signupLoading } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    username: '',
    sex: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name required';
    if (!formData.username.trim()) newErrors.username = 'Username required';
    if (!formData.sex) newErrors.sex = 'Please select your sex';
    if (!formData.email.trim()) newErrors.email = 'Email required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.password) newErrors.password = 'Password required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be 6+ characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      console.log('Submitting form data:', formData);
      const result = await quickSignup(formData);
      
      if (result.success) {
        // Clear any previous errors
        setErrors({});
        const pendingCondoId = localStorage.getItem('pendingWaitlistCondoId');
        if (pendingCondoId) {
          localStorage.removeItem('pendingWaitlistCondoId');
          condoService.joinWaitlist(pendingCondoId).catch(() => {});
        }
        onNavigate('onboarding');
      } else {
        setErrors({ general: result.error || 'Signup failed' });
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Check if error has field-specific errors from backend
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.message || 'Signup failed. Please try again.' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10 flex items-center justify-center min-h-screen">
        <div className="max-w-md mx-auto">
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-coffee mb-2">Quick Sign Up</h1>
              <p className="text-coffee opacity-80">30 seconds to get started • Complete profile later</p>
            </div>

            {errors.general && (
              <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-coffee mb-2 font-semibold">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                    placeholder="First name"
                    disabled={signupLoading}
                  />
                  {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-coffee mb-2 font-semibold">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                    placeholder="Last name"
                    disabled={signupLoading}
                  />
                  {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

                {/* Username */}
                <div>
                <label className="block text-coffee mb-2 font-semibold">Username *</label>
                <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => updateField('username', e.target.value.toLowerCase().replace(/\s/g, ''))}
                    className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                    placeholder="Choose a unique username"
                    disabled={signupLoading}
                />
                {errors.username && <p className="text-red-600 text-sm mt-1">{errors.username}</p>}
                </div>

                {/* Sex */}
                <div>
                <label className="block text-coffee mb-2 font-semibold">Sex *</label>
                <div className="grid grid-cols-2 gap-4">
                    <button
                    type="button"
                    onClick={() => updateField('sex', 'male')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                        formData.sex === 'male'
                        ? 'bg-steel border-steel text-white'
                        : 'bg-white border-coffee text-coffee hover:bg-cream'
                    }`}
                    disabled={signupLoading}
                    >
                    Male
                    </button>
                    <button
                    type="button"
                    onClick={() => updateField('sex', 'female')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                        formData.sex === 'female'
                        ? 'bg-terracotta border-terracotta text-white'
                        : 'bg-white border-coffee text-coffee hover:bg-cream'
                    }`}
                    disabled={signupLoading}
                    >
                    Female
                    </button>
                </div>
                {errors.sex && <p className="text-red-600 text-sm mt-1">{errors.sex}</p>}
                </div>

              {/* Email */}
              <div>
                <label className="block text-coffee mb-2 font-semibold">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                  placeholder="your.email@example.com"
                  disabled={signupLoading}
                />
                {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-coffee mb-2 font-semibold">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                  placeholder="8+ characters"
                  disabled={signupLoading}
                />
                {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
              </div>

              {/* Phone (Optional) */}
              <div>
                <label className="block text-coffee mb-2 font-semibold">Phone (Optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', formatPhone(e.target.value))}
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                  placeholder="(XXX) XXX-XXXX"
                  disabled={signupLoading}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={signupLoading}
                className="w-full bg-terracotta text-white py-4 rounded-lg font-bold text-lg hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 border-2 border-coffee shadow-lg"
              >
                {signupLoading ? '🚀 Creating Account...' : '🚀 Create Account & Continue'}
              </button>
            </form>

            <div className="text-center mt-6">
              <p className="text-coffee text-sm">
                Already have an account? <button onClick={() => onNavigate('login')} className="text-coffee hover:text-terracotta underline decoration-terracotta">Sign In</button>
              </p>
              <p className="text-coffee opacity-70 text-xs mt-2">
                By signing up, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>

          {/* Back button */}
        </div>
      </div>
    </div>
  );
};

export default QuickSignupPage;